import { fn } from '@ember/helper';
/* eslint-disable ember/no-tracked-properties-from-args */
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import MarketCustomReportingSidebar from 'lumen/components/market/custom-reporting/sidebar';
import type { SidebarCategory } from 'lumen/controllers/market/custom-reporting';
import type { LookerDashboard, LookerFolder } from 'lumen/models';

interface MarketCustomReportingFormMoveSignature {
  Args: {
    afterMove?: (dashboard: LookerDashboard) => void;
    categories: SidebarCategory[];
    close: () => void;
    item: LookerFolder | LookerDashboard;
    type: string;
  };
}

export default class MarketCustomReportingFormMove extends Component<MarketCustomReportingFormMoveSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];

  @tracked selectedFolder = this.args.item.folder;

  moveDashboardTask = task({ drop: true }, async (): Promise<void> => {
    const dashboard = this.args.item as LookerDashboard;
    const { selectedFolder } = this;
    const { afterMove, close } = this.args;

    try {
      const movedDashboard = (await dashboard.move({
        folder_id: selectedFolder.id,
      })) as LookerDashboard;

      afterMove?.(movedDashboard);
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(e);
    }

    close();
  });

  moveFolderTask = task({ drop: true }, async (): Promise<void> => {
    const { selectedFolder } = this;
    const folder = this.args.item as LookerFolder;
    const currentFolder = folder.folder;
    const { close } = this.args;

    try {
      folder.folder = selectedFolder;

      await folder.save();
    } catch (e) {
      folder.rollbackAttributes();
      folder.folder = currentFolder;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(e);
    }

    close();
  });

  moveTask = task({ drop: true }, async (): Promise<void> => {
    const { type } = this.args;

    if (type === 'dashboard') {
      await this.moveDashboardTask.perform();
      return;
    }

    await this.moveFolderTask.perform();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredCategories() {
    return this.args.categories.filter(({ type }) => type !== 'templates');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMoveDisabled() {
    const { selectedFolder } = this;

    if (!selectedFolder) {
      return false;
    }

    const { item } = this.args;

    if (!selectedFolder.canEdit) {
      return true;
    }

    if (this.args.type === 'dashboard') {
      return (
        selectedFolder.id ===
          (item as LookerDashboard).belongsTo('folder').id() ||
        selectedFolder.isEmbedSharedRoot
      );
    }

    const folderNames = selectedFolder.folders.map((folder) =>
      folder.name?.toLocaleLowerCase(),
    );

    return (
      folderNames.includes((item as LookerFolder).name.toLocaleLowerCase()) ||
      selectedFolder.isEmbedSharedRoot ||
      selectedFolder.id === item.id ||
      selectedFolder.id === (item as LookerFolder).belongsTo('folder').id()
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedDashboard() {
    if (this.args.type === 'dashboard') {
      return this.args.item as LookerDashboard;
    }

    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get title() {
    const { item } = this.args;

    return this.intl.t('custom_reporting.form.move.title', {
      title: 'title' in item ? item.title : item.name,
    });
  }

  <template>
    <PrismModal
      @onClose={{@close}}
      @title={{this.title}}
      class="custom-reporting__modal"
      data-test-id="move-modal"
    >
      <:default>
        <div>
          <MarketCustomReportingSidebar
            @categories={{this.filteredCategories}}
            @selectFolder={{fn (mut this.selectedFolder)}}
            @selectedDashboard={{this.selectedDashboard}}
            @selectedFolder={{this.selectedFolder}}
            @viewOnly={{true}}
          />
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-300 pr-500 pl-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@close}}
          />
          <PrismButton
            @isDisabled={{or this.isMoveDisabled this.moveTask.isRunning}}
            @isLoading={{this.moveTask.isRunning}}
            @label={{t "custom_reporting.move_to_location"}}
            @onClick={{this.moveTask.perform}}
            data-test-id="move-confirm-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
